/**
 * Button — Design System §5
 *
 * Variants: primary | secondary | ghost | destructive
 * Sizes:    sm (34px) | md (42px) | lg (52px)
 * Radius:   radius-sm (8px) — never pill-shaped
 * Primary:  solid black/white (inverts in dark mode via CSS vars)
 */

import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  loading?: boolean;
  children: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button({
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled,
  children,
  style,
  className,
  ...rest
}: ButtonProps, ref) {
  const isDisabled = disabled || loading;

  // Visual states (hover/active/disabled) live in tokens.css under .ui-btn —
  // inline styles can't express them, and the tokens sheet is the system's home.
  // While loading, the label stays rendered next to the spinner so the button
  // keeps its accessible name at the exact moment the action is committing.
  return (
    <button
      ref={ref}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      data-loading={loading ? 'true' : undefined}
      className={className ? `ui-btn ${className}` : 'ui-btn'}
      data-variant={variant}
      data-size={size}
      style={style}
      {...rest}
    >
      {loading && <Spinner size={size} />}
      {children}
    </button>
  );
});

function Spinner({ size }: { size: ButtonSize }) {
  const dim = size === 'sm' ? 12 : 16;
  return (
    <svg
      width={dim}
      height={dim}
      viewBox="0 0 16 16"
      fill="none"
      aria-hidden="true"
      style={{ animation: 'spin 0.75s linear infinite' }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="2" strokeOpacity="0.3" />
      <path d="M14 8A6 6 0 0 0 8 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}
